/**
 * Canonical post-kind resolution for display surfaces.
 *
 * After the unified-posts migration (`backend/sql/migration/2026031801_unify_posts.sql`)
 * and the entity-view rewrite (`2026031804_entity_views.sql`), teabates are
 * first-class `posts.kind = 'teabate'` rows and the entity API surfaces them
 * as `entity_type = 'teabate'`. The legacy `properties.is_teabate` boolean is
 * no longer written and no longer read; use `entity.kind` exclusively.
 */
import type { PostDisplayKind, PostKindResolverEntity } from "components/Posts/display/postDisplayTypes";

/**
 * Returns true when a record should render as a teabate.
 */
export function isTeabateEntity(entity?: PostKindResolverEntity | null): boolean {
  if (!entity) {
    return false;
  }

  return entity.kind === "teabate";
}

/**
 * Normalizes mixed entity types to the canonical post display kind.
 */
export function resolvePostKind(
  entityType: string,
  entity?: PostKindResolverEntity | null
): PostDisplayKind | null {
  switch (entityType) {
    case "solution":
    case "feedback":
    case "action":
    case "teabate":
      return entityType;
    case "impact":
      // If the upstream feed misclassified a teabate row as `entity_type='impact'`
      // (extremely rare post-migration; only possible if a caller hand-crafts a
      // request), trust the canonical `kind` field on the entity.
      return entity?.kind === "teabate" ? "teabate" : "impact";
    default:
      return null;
  }
}

/**
 * Guards for surfaces that only support post-backed kinds.
 */
export function isPostDisplayKind(
  entityType: string,
  entity?: PostKindResolverEntity | null
): boolean {
  return resolvePostKind(entityType, entity) !== null;
}
